<template>
  <div class="forgot-password-container">
    <form @submit.prevent="handleSubmit">
      <h2>重置密码</h2>
      
      <!-- 第一步：输入邮箱 -->
      <div v-if="step === 1">
        <div class="form-group">
          <label for="email">邮箱地址</label>
          <input 
            type="email" 
            id="email" 
            v-model="email" 
            required
            placeholder="请输入注册邮箱"
          />
        </div>
        
        <div class="form-group">
          <button type="submit" :disabled="isLoading || isSolvingPow">
            {{ isSolvingPow ? '验证中...' : (isLoading ? '发送中...' : '发送验证码') }}
          </button>
        </div>
      </div>
      
      <!-- 第二步：输入验证码和新密码 -->
      <div v-if="step === 2">
        <div class="email-info">
          验证码已发送至：{{ email }}
        </div>
        
        <div class="form-group">
          <label for="code">验证码</label>
          <input 
            type="text" 
            id="code" 
            v-model="code" 
            required
            placeholder="请输入6位验证码"
            maxlength="6"
          />
        </div>
        
        <div class="form-group">
          <label for="newPassword">新密码</label>
          <input 
            type="password" 
            id="newPassword" 
            v-model="newPassword" 
            required
            placeholder="请输入新密码（至少6位）"
            minlength="6"
          />
        </div>
        
        <div class="form-group">
          <label for="confirmPassword">确认密码</label>
          <input 
            type="password" 
            id="confirmPassword" 
            v-model="confirmPassword" 
            required
            placeholder="请再次输入新密码"
            minlength="6"
          />
        </div>
        
        <div class="form-group">
          <button type="submit" :disabled="isLoading">
            {{ isLoading ? '重置中...' : '重置密码' }}
          </button>
        </div>
        
        <div class="resend-code">
          <span v-if="countdown > 0">{{ countdown }}秒后可重新发送</span>
          <a v-else @click.prevent="resendCode" href="#">重新发送验证码</a>
        </div>
      </div>

      <div class="form-group pow-group">
        <label>安全验证</label>
        <div v-if="isSolvingPow" class="pow-status">
          <span class="pow-spinner"></span>
          正在计算工作量证明...
        </div>
        <div v-else-if="powSolved" class="pow-status pow-solved">
          ✓ 安全验证已通过
        </div>
        <div class="pow-tip">
          {{ step === 1 ? '发送验证码时将自动完成工作量证明验证' : '如需重新发送验证码，将自动重新计算工作量证明' }}
        </div>
      </div>
      
      <div v-if="error" class="error-message">
        {{ error }}
      </div>
      
      <div v-if="success" class="success-message">
        {{ success }}
      </div>
      
      <div class="back-to-login">
        <router-link to="/login">返回登录</router-link>
      </div>
      
      <div class="icp-info">
        <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
          豫ICP备2025139425号-1
        </a>
      </div>
    </form>
  </div>
</template>

<script>
import { API_BASE_URL } from '../config/api';
import { solvePowChallenge } from '../utils/pow';

export default {
  name: 'ForgotPassword',
  data() {
    return {
      step: 1,
      email: '',
      code: '',
      newPassword: '',
      confirmPassword: '',
      isLoading: false,
      isSolvingPow: false,
      powSolved: false,
      error: null,
      success: null,
      countdown: 0,
      countdownTimer: null
    };
  },
  methods: {
    async handleSubmit() {
      this.error = null;
      this.success = null;
      
      if (this.step === 1) {
        await this.sendResetCode();
      } else {
        await this.resetPassword();
      }
    },
    
    async sendResetCode() {
      this.isLoading = true;
      this.isSolvingPow = true;
      this.powSolved = false;
      
      try {
        const { challenge, nonce } = await solvePowChallenge();
        this.powSolved = true;
        this.isSolvingPow = false;
        
        const response = await fetch(`${API_BASE_URL}/user/send-password-reset-code`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: this.email,
            pow_challenge: challenge,
            pow_nonce: nonce
          })
        });
        
        const result = await response.json();
        
        if (response.ok) {
          this.success = result.message;
          this.step = 2;
          this.startCountdown();
        } else {
          this.powSolved = false;
          this.error = result.message;
        }
      } catch (err) {
        console.error('发送验证码失败:', err);
        this.powSolved = false;
        this.error = err.message || '发送验证码失败，请稍后重试';
      } finally {
        this.isSolvingPow = false;
        this.isLoading = false;
      }
    },
    
    async resetPassword() {
      if (this.newPassword !== this.confirmPassword) {
        this.error = '两次输入的密码不一致';
        return;
      }
      
      if (this.newPassword.length < 6) {
        this.error = '密码长度至少6位';
        return;
      }
      
      this.isLoading = true;
      
      try {
        const response = await fetch(`${API_BASE_URL}/user/reset-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: this.email,
            code: this.code,
            new_password: this.newPassword
          })
        });
        
        const result = await response.json();
        
        if (response.ok) {
          this.success = result.message + '，即将跳转到登录页面...';
          setTimeout(() => {
            this.$router.push('/login');
          }, 3000);
        } else {
          this.error = result.message;
        }
      } catch (err) {
        console.error('重置密码失败:', err);
        this.error = '重置密码失败，请稍后重试';
      } finally {
        this.isLoading = false;
      }
    },
    
    async resendCode() {
      this.error = null;
      this.success = null;
      await this.sendResetCode();
    },
    
    startCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
      }

      this.countdown = 60;
      this.countdownTimer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(this.countdownTimer);
          this.countdownTimer = null;
        }
      }, 1000);
    }
  },
  mounted() {
  },
  beforeUnmount() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  }
};
</script>

<style scoped>
.forgot-password-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  padding: 20px;
  box-sizing: border-box;
}

.forgot-password-container form {
  background: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 450px;
}

h2 {
  text-align: center;
  margin-bottom: 30px;
  color: #333;
  font-size: 24px;
  font-weight: 500;
}

.form-group {
  margin-bottom: 20px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #555;
  font-size: 14px;
}

input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  background: white;
  box-sizing: border-box;
}

input:focus {
  outline: none;
  border-color: #007bff;
}

button {
  width: 100%;
  padding: 12px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: background-color 0.3s ease;
}

button:hover {
  background: #0056b3;
}

button:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.email-info {
  background: #d4edda;
  color: #155724;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-size: 14px;
  text-align: center;
}

.error-message {
  color: #dc3545;
  margin-top: 10px;
  padding: 10px;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  font-size: 14px;
}

.success-message {
  color: #155724;
  margin-top: 10px;
  padding: 10px;
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 4px;
  font-size: 14px;
}

.pow-group {
  margin-bottom: 20px;
}

.pow-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #f8f9fa;
  font-size: 14px;
  color: #555;
}

.pow-status.pow-solved {
  color: #155724;
  background: #d4edda;
  border-color: #c3e6cb;
}

.pow-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #ccc;
  border-top-color: #007bff;
  border-radius: 50%;
  animation: pow-spin 0.6s linear infinite;
}

@keyframes pow-spin {
  to {
    transform: rotate(360deg);
  }
}

.pow-tip {
  margin-top: 8px;
  color: #666;
  font-size: 12px;
  line-height: 1.5;
  text-align: center;
}

.resend-code {
  text-align: center;
  margin-top: 15px;
  font-size: 14px;
  color: #666;
}

.resend-code a {
  color: #007bff;
  text-decoration: none;
}

.resend-code a.disabled {
  color: #6c757d;
  pointer-events: none;
  cursor: not-allowed;
  text-decoration: none;
}

.resend-code a:hover {
  text-decoration: underline;
}

.back-to-login {
  text-align: center;
  margin-top: 20px;
  color: #666;
  font-size: 14px;
}

.back-to-login a {
  color: #007bff;
  text-decoration: none;
}

.back-to-login a:hover {
  text-decoration: underline;
}

.icp-info {
  text-align: center;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

.icp-info a {
  color: #999;
  text-decoration: none;
  font-size: 12px;
  transition: color 0.3s ease;
}

.icp-info a:hover {
  color: #666;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .forgot-password-container {
    padding: 20px 15px;
    min-height: 100vh;
    justify-content: center;
  }
  
  .forgot-password-container form {
    padding: 40px 25px;
    margin: 0;
    width: 100%;
    max-width: 400px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  }
  
  h2 {
    font-size: 24px;
    margin-bottom: 30px;
  }
  
  input {
    padding: 14px;
    font-size: 16px; /* 防止iOS缩放 */
    border-radius: 8px;
  }
  
  button {
    padding: 14px;
    font-size: 16px;
    border-radius: 8px;
  }
  
  .send-code-btn {
    padding: 14px 16px;
    font-size: 14px;
    min-width: 120px;
  }
  
  .email-input-group {
    flex-direction: column;
    gap: 12px;
  }
  
  .send-code-btn {
    width: 100%;
  }
  
  .step-indicator {
    margin-bottom: 25px;
  }
  
  .step {
    padding: 8px 16px;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .forgot-password-container {
    padding: 15px 10px;
  }
  
  .forgot-password-container form {
    padding: 30px 20px;
    max-width: none;
    margin: 0 -5px;
  }
  
  h2 {
    font-size: 22px;
    margin-bottom: 25px;
  }
  
  input {
    padding: 12px;
    font-size: 16px;
  }
  
  button {
    padding: 12px;
    font-size: 16px;
  }
  
  .send-code-btn {
    padding: 12px 16px;
    font-size: 14px;
  }
  
  .step {
    padding: 6px 12px;
    font-size: 13px;
  }
  
  .form-group {
    margin-bottom: 18px;
  }
}

/* 触摸设备优化 */
@media (hover: none) and (pointer: coarse) {
  button:hover {
    background: #007bff;
  }
  
  button:active {
    background: #0056b3;
    transform: scale(0.98);
  }
  
  .send-code-btn:hover:not(:disabled) {
    background: #28a745;
  }
  
  .send-code-btn:active:not(:disabled) {
    background: #1e7e34;
    transform: scale(0.98);
  }
  
  input {
    -webkit-appearance: none;
  }
  
  .resend-code a:hover,
  .back-to-login a:hover {
    text-decoration: none;
  }
  
  .resend-code a:active,
  .back-to-login a:active {
    color: #0056b3;
  }
}
</style>